import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import { buildPaymentUrl, formatMoney, makeOrderNumber } from "../utils/commerce";
import brandConfig from "../config";
import "../css/checkout.css";

export const PENDING_PAYMENT_KEY = "anackmira_pending_payment";

const CheckOut = () => {
  const { cartItems, cartTotal } = useContext(CartContext);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const items = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((game) => game.id === item.id),
    }))
    .filter((item) => item.product);

  const submit = (event) => {
    event.preventDefault();
    if (locked.current) return;

    const cleanEmail = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    if (!items.length || cartTotal <= 0) {
      setError("Your cart is empty. Return to the shop before checking out.");
      return;
    }

    locked.current = true;
    setSubmitting(true);
    setError("");

    const orderNo = makeOrderNumber();
    const currency = items[0].product.currency || brandConfig.currency;
    const payment = {
      orderNo,
      email: cleanEmail,
      amount: cartTotal,
      currency,
      title: items[0].product.name,
    };

    try {
      window.sessionStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(payment));
      const paymentUrl = buildPaymentUrl({
        origin: window.location.origin,
        ...payment,
      });
      window.location.assign(paymentUrl);
    } catch (paymentError) {
      setError("Payment could not be started. Please try again.");
      setSubmitting(false);
      locked.current = false;
    }
  };

  if (!items.length) {
    return (
      <main className="checkout-page page-shell">
        <div className="empty-cart">
          <span>!</span>
          <p className="eyebrow">Checkout unavailable</p>
          <h1>Your cart is empty.</h1>
          <p>Add a game to your cart before checking out.</p>
          <Link className="button" to="/shop">Browse games</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page page-shell">
      <div className="checkout-heading">
        <Link to="/cartpage">← Return to cart</Link>
        <p className="eyebrow">Secure checkout</p>
        <h1>Complete your order.</h1>
        <p>Enter your contact email, then continue to our secure payment page.</p>
      </div>
      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={submit} noValidate>
          <section>
            <span className="form-step">01</span>
            <div>
              <h2>Contact information</h2>
              <p>We will use this email for your order confirmation.</p>
            </div>
            <label htmlFor="checkout-email">Email address</label>
            <input
              id="checkout-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              aria-invalid={Boolean(error)}
            />
            {error && <div className="form-error" role="alert">{error}</div>}
            <button
              className="button button--large button--full"
              type="submit"
              disabled={submitting}
            >
              {submitting ? <><span className="spinner" /> Redirecting…</> : `Pay now · ${formatMoney(cartTotal, brandConfig.currency, brandConfig.locale)}`}
            </button>
            <small>Shipping and card details are not collected on this page.</small>
          </section>
        </form>
        <aside className="checkout-summary">
          <p className="eyebrow">Order summary</p>
          {items.map(({ product }) => (
            <div className="checkout-item" key={product.id}>
              <img src={product.coverImage} alt="" />
              <div><strong>{product.name}</strong><span>Full Browser Edition</span></div>
              <b>{formatMoney(product.price, product.currency, brandConfig.locale)}</b>
            </div>
          ))}
          <div className="summary-total">
            <span>Total</span>
            <strong>{formatMoney(cartTotal, brandConfig.currency, brandConfig.locale)}</strong>
          </div>
          <ul>
            <li>✓ No shipping or delivery fee</li>
            <li>✓ Immediate digital access after payment</li>
            <li>✓ Secure payment on the next page</li>
          </ul>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
